import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Location, UserService, LocationService, PaymentData, AppService, OrderHalfItem, MenuData, Guid } from 'briteatlib';
import { BehaviorSubject, ReplaySubject, Subject, combineLatest, filter, firstValueFrom, timeout } from 'rxjs';
import { MenuService } from './menu.service';
import { MenuItemData, Order, OrderItemOption, OrderMenuItem, OrderPromotionItem } from 'briteatlib';
import { Promotion } from 'briteatlib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const CurrentPostcodeStorageKey = 'OrderService_currentPostcode';
export type getReadyToOrderMode = 'ready' | 'set-order-type' | 'set-location' | 'delivery-unavailable' | 'business-closure';
export type panelMode = 'app' | 'pos';
@Injectable({
  providedIn: 'root'
})
export class OrderService {
  currentOrder$ = new ReplaySubject<Order>(1);
  currentLocation: Location;
  currentPostcode$ = new ReplaySubject<string>(1);
  currentOrderTime$ = new BehaviorSubject<Date | 'ASAP'>(null);
  deliveryZone$ = new BehaviorSubject<any>(null);
  showGetReadyToOrder$ = new Subject<getReadyToOrderMode>();
  customizeOrderItem$ = new Subject<{
    item: OrderMenuItem;
    mode: 'add' | 'update',
    panelMode: panelMode,
    callback?: (action: string, item: OrderMenuItem) => void,
    maxQuantity?: number,
    hidePrice?: boolean
  }>();
  customizePromotion$ = new Subject<{ menu?: MenuData; promotion?: Promotion; orderItem?: OrderPromotionItem, panelMode?: panelMode }>();
  customizeHalfItem$ = new Subject<{ menu?: MenuData; orderItem?: OrderHalfItem, panelMode?: panelMode }>();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private appService: AppService,
    private menuService: MenuService,
    private locationService: LocationService
  ) {
    if (appService.isBrowser) {
      if (!appService.pos) this.initializeCurrentOrder();
    }
  }

  private initializeCurrentOrder() {
    this.loadCurrentLocation();

    this.currentPostcode$
      .pipe(takeUntilDestroyed())
      .subscribe(postcode => this.persistCurrentLocation(postcode?.trim() || null));

    combineLatest([this.userService.current$, this.appService.activeBusiness$, this.currentPostcode$, this.currentOrderTime$])
      .pipe(takeUntilDestroyed(), filter(([user, business, postcode, time]) => !!business))
      .subscribe(async ([user, business, postcode, time]) => {
        if (this.currentLocation?.Postcode != postcode) {
          this.currentLocation = postcode == null ? null : (
            user?.Profile?.Addresses?.find(_ => _?.Postcode === postcode)
            || await this.locationService.getPostcode(postcode)
          );
        }
        await this.setCurrentOrderInfo();
        let zones = postcode ? await this.availableDeliveryZones() : [];
        this.deliveryZone$.next(zones?.find(_ => !!_));
        // await this.checkReadyToOrder();
        await this.getCurrentOrderForUser();
      });
    this.menuService.orderType$.pipe(takeUntilDestroyed()).subscribe(() => this.setCurrentOrderInfo());

    combineLatest([this.menuService.orderType$, this.deliveryZone$, this.currentOrder$])
      .subscribe(async () => await this.checkReadyToOrder());
  }

  currentOrderBusy$ = new BehaviorSubject<boolean>(false);
  private _currentOrder: Order;
  set currentOrder(order: Order) {
    this.currentOrderBusy$.next(false);
    this._currentOrder = order;
    this.currentOrder$.next(order);
  }
  get currentOrder() { return this._currentOrder; }

  get currentOrderTime(): Date {
    if (this.currentOrder?.ScheduledDate)
      return new Date(this.currentOrder.ScheduledDate);
    if (this.currentOrderTime$.value == 'ASAP')
      return new Date(this.appService.operatingHours?.[this.menuService.orderType$.value]?.fromDate.getTime());
    return this.currentOrderTime$.value;
  }

  // User Order Methods [start]
  private loadCurrentLocation() {
    let postcode = localStorage.getItem(CurrentPostcodeStorageKey);
    this.currentPostcode$.next(postcode);
  }
  private persistCurrentLocation(postcode: string) {
    if (postcode) localStorage.setItem(CurrentPostcodeStorageKey, postcode);
    else localStorage.removeItem(CurrentPostcodeStorageKey);
  }

  async getCurrentOrderForUser() {
    this.currentOrderBusy$.next(true);
    this.currentOrder = await firstValueFrom(this.http.post<Order>(`/api/order/current`, null));
  }

  async cancelOrder(orderId?: string) {
    this.currentOrderBusy$.next(true);
    const order = await firstValueFrom(this.http.post<Order>(`/api/order/cancel${orderId ? '/' + orderId : ''}`, null));
    if (!orderId || orderId == this.currentOrder?.Id) this.currentOrder = null;
    return order;
  }

  async payCashCurrent() {
    this.currentOrderBusy$.next(true);
    await firstValueFrom(this.http.post<void>(`/api/order/current/pay-cash`, null));
    this.currentOrder = null;
  }

  private async editUserOrderItem(addMenuItem?: OrderMenuItem, addPromotionItem?: OrderPromotionItem, addHalfItem?: OrderHalfItem, removeFingerprint?: string) {
    await this.checkReadyToOrder(async () => {
      let type = this.currentOrder?.Type;
      if (!type || type == 'Default') type = this.menuService.orderType$.value;
      const location = this.currentLocation;
      const scheduledDate = this.currentOrderTime;

      this.currentOrderBusy$.next(true);
      this.currentOrder = await firstValueFrom(this.http.post<Order>(`/api/order/current/set`, {
        type, location, scheduledDate,
        addMenuItem, addPromotionItem, addHalfItem,
        removeFingerprint
      }));
    });
  }

  async setCurrentOrderInfo(data?: any) {
    if (this.currentOrder == null) return;
    this.currentOrderBusy$.next(true);
    this.currentOrder = await firstValueFrom(this.http.post<Order>(`/api/order/current/set`,
      data || { type: this.menuService.orderType$.value, location: this.currentLocation, scheduledDate: this.currentOrderTime }));
  }

  // Applies (or clears, when code is empty) a next-order promo code to the current
  // order. The server re-validates the code and returns the recalculated order, so
  // an invalid code comes back with PromoCode cleared and a PromoDiscount of 0.
  async applyPromoCode(code: string) {
    if (this.currentOrder == null) return this.currentOrder;
    this.currentOrder.PromoCode = code ?? '';
    await this.setCurrentOrderInfo(this.currentOrder);
    return this.currentOrder;
  }
  // User Order Methods [end]

  private async updatePosOrder(addMenuItem?: OrderMenuItem, addPromotionItem?: OrderPromotionItem, addHalfItem?: OrderHalfItem, removeFingerprint?: string) {
    this.currentOrderBusy$.next(true);
    try {
      this.currentOrder = await firstValueFrom(this.http.post<Order>(`/api/order/pos/${this.currentOrder?.Id || Guid.newGuid()}`, {
        addMenuItem, addPromotionItem, addHalfItem,
        removeFingerprint
      }));
      return this.currentOrder;
    } finally {
      this.currentOrderBusy$.next(false);
    }
  }

  async editOrderItem(addMenuItem?: OrderMenuItem, addPromotionItem?: OrderPromotionItem, addHalfItem?: OrderHalfItem, removeFingerprint?: string) {
    if (!this.appService.pos) return this.editUserOrderItem(addMenuItem, addPromotionItem, addHalfItem, removeFingerprint);
    return this.updatePosOrder(addMenuItem, addPromotionItem, addHalfItem, removeFingerprint);
  }

  async getPOSOrders(criteria: any) {
    return firstValueFrom(this.http.post<{ List: Order[], Total: number }>(`/api/order/pos/list`, criteria));
  }

  async getPOSOrdersStatuses(criteria: any) {
    return firstValueFrom(this.http.post<{ Key: string, DocCount: number }[]>(`/api/order/pos/agg/status`, criteria));
  }

  private resolvePromotionValue(price: any): number {
    if (!price) return 0;
    let type = this.currentOrder?.Type;
    if (!type || type == 'Default') type = this.menuService.orderType$.value;
    const override =
      type == 'Delivery' ? price.DeliveryValue :
      type == 'Pickup' ? price.PickupValue :
      type == 'DineIn' ? price.DineInValue : null;
    return override ?? price.Value;
  }

  // A condition's option ids are the allowed choices per option group (the server
  // narrows each group to them the same way), so several sizes on one condition
  // mean "any of these sizes", not "all of them at once".
  private matchesConditionOptions(optionIds: string[], item: OrderMenuItem): boolean {
    if (!optionIds?.length) return true;
    const selected = (id: string) => item.MenuOptions?.some(o => o.OptionId == id);
    const groups = item._MenuItem?.Options;
    if (!groups?.length) return optionIds.every(selected);
    return groups.every(group => {
      const allowed = group.Choices?.filter(ch => optionIds.includes(ch.MenuOptionId));
      return !allowed?.length || allowed.some(ch => selected(ch.MenuOptionId));
    });
  }

  calcPromotionItem(promotion: Promotion, promotionItem: OrderPromotionItem) {
    promotionItem.PromotionId = promotion.Id;
    promotionItem.MenuItems.forEach(_ => this.calcOrderItem(_));

    promotion.Items?.forEach(item => {
      item.Conditions?.forEach(condition => {
        condition._MenuItems = promotionItem.MenuItems.filter(_ =>
          (!condition.MenuIds?.length || condition.MenuIds.includes(_.MenuId)) &&
          (!condition.MenuItemIds?.length || condition.MenuItemIds?.some(mi => mi == _.ItemId)) &&
          this.matchesConditionOptions(condition.MenuOptionIds, _)
        );
        condition._Ordered = condition._MenuItems.reduce((a, b) => a + b.Quantity, 0);
        condition._Fullfilled = condition._Ordered == condition.Quantity;
        if (!condition._Fullfilled)
          condition._MenuItemsData?.forEach(_ => this.menuService.calcItem(_));
      });
      item._Fullfilled = item.Conditions?.some(_ => _._Fullfilled);
      item._MenuItems = item.Conditions.flatMap(_ => _._MenuItems);
      item.Conditions?.forEach((_, i) => _._UnOrderable = item.Conditions.some((__, j) => j != i && __._Ordered > 0));
    });

    // An OrderMenuItem can represent more than one unit. Expand it before
    // selecting the discounted items so a quantity of two correctly receives
    // the buy-one-get-one discount.
    var items = promotionItem.MenuItems
      .flatMap(item => Array.from({ length: item.Quantity }, () => ({
        item,
        price: item.Price / item.Quantity
      })))
      .sort((a, b) => a.price - b.price);
    var discountCount = Math.floor(items.length / (promotion.DiscountCheapest ? 2 : 1));

    promotionItem.OriginalPrice = items.reduce((a, b) => a + b.price, 0);
    const priceValue = this.resolvePromotionValue(promotion.Price);
    switch (promotion.Price?.DiscountType) {
      case 'FixPrice':
        var extra = items.slice(0, discountCount).map(_ => _.item._MenuItem.Options.filter(o => !o.IsVariation)).flat()
          .reduce((a, b) => a + b._Order.OrderPrice, 0);
        promotionItem.Price = priceValue + extra + items.slice(discountCount).reduce((a, b) => a + b.price, 0);
        break;
      case 'DiscountPercentage':
        promotionItem.Price = items.slice(0, discountCount).reduce((a, b) => a + b.price, 0) * (100 - priceValue) / 100
          + items.slice(discountCount).reduce((a, b) => a + b.price, 0);
        break;
      case 'DiscountAmount':
        promotionItem.Price = promotionItem.OriginalPrice - priceValue;
        break;
    }

    // Keep client-side totals in sync with the server guard: an offer must never
    // become free or negative when a fixed discount matches/exceeds its items.
    promotionItem.Price = Math.max(0.01, Math.round((promotionItem.Price || 0) * 100) / 100);
    promotionItem._ReadyToAdd = promotion.Items?.every(_ => _._Fullfilled);
  }

  calcHalfItem(menu: MenuData, halfItem: OrderHalfItem) {
    halfItem.MenuItems.forEach(_ => this.calcOrderItem(_));
    halfItem.Price = halfItem.MenuItems.reduce((a, b) => Math.max(a, b.Price), 0) + menu.HalfAndHalfPrice;
    const hasDisabledHalfAndHalfChoice = halfItem.MenuItems?.some(item =>
      item._MenuItem?.Options?.some(option => option.Choices?.some(choice =>
        choice.DisabledForHalfAndHalf && item.MenuOptions?.some(menuOption => menuOption.OptionId === choice.MenuOptionId)
      ))
    );
    halfItem._ReadyToAdd = halfItem.MenuItems?.length === 2 && !hasDisabledHalfAndHalfChoice && !halfItem.MenuItems.some(_ => !_._ReadyToAdd);
  }

  calcOrderItem(item: OrderMenuItem) {
    if (!item._MenuItem)
      item._MenuItem = this.menuService.getMenuItem(item.MenuId, item.ItemId, item.MenuOptions);
    this.menuService.calcItem(item._MenuItem);
    item.Price = item._MenuItem._Order.OrderPrice * item.Quantity;
    item._ReadyToAdd = item._MenuItem._Order.ReadyToAdd;
  }

  createOrderItem(item: MenuItemData, quantity = 1): OrderMenuItem {
    this.menuService.calcItem(item);
    return {
      _MenuItem: item,
      ItemId: item.Id,
      Name: item.Name,
      MenuId: item.MenuId,
      Quantity: quantity,
      MenuOptions: this.getMenuOptions(item, true)
    };
  }

  getMenuOptions(item: MenuItemData, addDefault = false): OrderItemOption[] {
    return item.Options?.flatMap(o => o.Choices?.filter(ch => ch._Order.Quantity > 0 || ch.IsDefault)
      .map(ch => {
        if (addDefault && ch.IsDefault && !ch._Order.Quantity) ch._Order.Quantity = 1;
        return ({ Name: ch.Name, OptionId: ch.MenuOptionId, Quantity: ch._Order.Quantity });
      }));
  }

  onReady: () => void;
  private checkReadyToOrderToken = 0;
  private async checkReadyToOrder(onReady?: () => void): Promise<void> {
    if (onReady) this.onReady = onReady;
    const token = ++this.checkReadyToOrderToken;
    let mode: getReadyToOrderMode = 'ready';
    var orderType = this.menuService.orderType$.value;
    if (!orderType || orderType == 'Default')
      mode = 'set-order-type';
    else if (this.appService.operatingHours?.[orderType]?.state == 'closed'
      && !(this.appService.operatingHours?.[orderType]?.fromDate <= this.currentOrderTime))
      mode = 'business-closure';
    else if (orderType == 'Delivery') {
      // Let customers browse the menu and build a delivery order before asking
      // for their postcode. The basket requests it when they start checkout.
      if (this.currentLocation?.Postcode) {
        const zones = await this.availableDeliveryZones();
        if (token != this.checkReadyToOrderToken) return; // a newer check superseded this one
        if (!(zones?.length > 0)) {
          mode = 'delivery-unavailable';
        }
      }
    }
    if (token != this.checkReadyToOrderToken) return; // a newer check superseded this one

    this.showGetReadyToOrder$.next(mode);
    if (mode == 'ready' && this.onReady) {
      let method = this.onReady;
      setTimeout(() => method());
      this.onReady = null;
    }
  }

  searchMyOrder(criteria: any) {
    return firstValueFrom(this.http.post<{ List: Order[], Total: number }>(`/api/order/mine`, criteria));
  }
  getMyOrder(id: string) {
    return firstValueFrom(this.http.get<Order>(`/api/order/mine/${id}`));
  }

  // searchOrder(criteria: any) {
  //   return firstValueFrom(this.http.post<{ List: Order[], Total: number }>(`/api/order/${this.businessId}/search`, criteria));
  // }

  getOrder(id: string, calculate = false) {
    return firstValueFrom(this.http.get<any>(`/api/order/get/${id}`, { params: { calculate } }));
  }
  saveOrder(order: any) {
    return firstValueFrom(this.http.post<Order>(`/api/order/save`, order));
  }

  checkoutPosOrder(orderId: string, paymentData: any) {
    return firstValueFrom(this.http.post<Order>(`/api/order/pos/${orderId}/checkout`, paymentData));
  }

  async availableDeliveryZones(): Promise<any[]> {
    var business = this.appService.activeBusiness;
    var postcode = this.currentLocation.Postcode;
    if (!business?.DeliveryZones?.length || !postcode) return null;
    var outcode = postcode.split(' ')[0];
    var zones: any[] = business.DeliveryZones.filter((_: any) => _.Postcodes?.some(p => postcode == p || outcode == p.split(' ')[0]));
    if (zones.length == 0) {
      var distance = await this.locationService.distance(business.Location.Postcode, postcode);
      zones = business.DeliveryZones.filter(dz => dz.MaxDistance >= distance);
    }

    if (!zones?.length) return null;
    return [...zones.sort((a, b) => a.DeliveryFee - b.DeliveryFee)];
  }

  async getPaymentData(id: string) {
    return firstValueFrom(this.http.get<PaymentData>(`/api/order/payment-data/${id}`).pipe(timeout(20000)));
  }

  reorder(id: string) {
    return firstValueFrom(this.http.post<Order>(`/api/order/reorder/${id}`, null));
  }

  setStatus(id: string, status: string, etaMinutes?: number, reason?: string) {
    return firstValueFrom(this.http.post<any>('/api/order/set-status', { orderId: id, status, etaMinutes, reason }));
  }
  printPreview(id: string) {
    return firstValueFrom(this.http.get(`/api/order/pdf/${id}`, { responseType: 'arraybuffer' }));
  }
  printPreviewImage(id: string) {
    return firstValueFrom(this.http.get(`/api/order/image/${id}`, { responseType: 'arraybuffer' }));
  }
  setPrintFlag(id: any) {
    return firstValueFrom(this.http.post<Order>(`/api/order/set-print-flag/${id}`, null));
  }
  getPendingOrderCount() {
    return firstValueFrom(this.http.post<number>('/api/order/pending-count', null));
  }


}
